import {
  convertStringToBoolean,
  normalizeIdentifier,
  selectedOptionTokens,
  isValidDateObject,
  formatAsTimeString,
  formatAsDateString,
  formatAsDateTimeStringWithoutTimezone,
  parseDurationStringIntoHHMM,
  castToBoolean,
} from "../util";
import { datesAreEqual, dateIsAfter, dateIsBefore } from "../dateutil";
import moment from "moment-timezone";

import angular from "angular";

export class LogicModifier {
  constructor() {
    const data = arguments[0] || {};
    this.id = data.id || null;
    this.target_id = data.target_id || null;
    this.target = data.target || null;
    this.current_field = data.current_field || null;
    this.logic = data.logic || "";
    this.possible_options = data.possible_options || [];

    // Options come back as a general `set_options` field and we need to filter them
    this.truthy_values =
      (data.set_options && data.set_options.filter((option) => castToBoolean(option.truthy_value)))
      || [];
    this.falsy_values =
      (data.set_options && data.set_options.filter((option) => !castToBoolean(option.truthy_value)))
      || [];

    // Delete the `truthy_value` field on each value so Angular will correctly pick up selected options
    this.truthy_values.forEach((value) => delete value.truthy_value);
    this.falsy_values.forEach((value) => delete value.falsy_value);

    this.value = data.value || "";
    this.selected_events_custom_options_id = data.selected_events_custom_options_id || null;
    this.valueOption = data.valueOption || null;
  }

  get DateTimeService() {
    const $injector = angular.injector(["ng", "recaps.services"]);
    return $injector.get("DateTimeService");
  }

  set target(target) {
    if (this._target === undefined) {
      this._target = target;
    } else if (this._target !== target) {
      // Setting new target
      this._target = target;
      // Remove any selected value option as it won't apply to this new target
      this.valueOption = null;
    }
  }

  get target() {
    return this._target;
  }

  set valueOption(valueOption) {
    if (this._valueOption === undefined) {
      // When first setting, don't persist to .value
      this._valueOption = valueOption;
    } else if (this._valueOption !== valueOption) {
      // When setting new valueOption, update .value as well
      this._valueOption = valueOption;

      if (valueOption && valueOption.id) {
        this.selected_events_custom_options_id = valueOption.id;
        this._value = valueOption.name;
      } else {
        this.selected_events_custom_options_id = null;
        this._value = null;
      }
    }
  }

  get valueOption() {
    return this._valueOption;
  }

  set value(value) {
    if (this._value === undefined) {
      // When first setting, don't clear .valueOption
      this._value = value;
    } else if (this._value !== value) {
      // When setting new value, clear .valueOption
      this._value = value;
      this._valueOption = null;
    }
  }

  get usesValue() {
    return this.logic !== "PRESENT";
  }

  get value() {
    return this._value;
  }

  get valueDescription() {
    const fieldType =
      this.target && this.target.field && this.target.field.fieldType;
    let description;
    if (fieldType && fieldType.isBoolean) {
      description = convertStringToBoolean(this.value) ? "Yes" : "No";
    } else if (fieldType && fieldType.isDate) {
      description = formatAsDateString(this.value);
    } else if (fieldType && fieldType.isDateTime) {
      const useAmPm = true;
      description = formatAsDateTimeStringWithoutTimezone(this.value, useAmPm);
    } else if (fieldType && fieldType.isTime) {
      const useAmPm = true;
      description = formatAsTimeString(this.value, useAmPm);
    } else if (this.valueOption) {
      description = this.valueOption.name;
    } else {
      description = this.value;
    }
    return description;
  }

  logicModificationResultForRecapResponse(recapResponse, modifyFieldResponse) {
    const id = (this.target && this.target.id) || this.target_id;
    const targetFieldResponse =
      recapResponse.getFieldResponseForFieldInstanceId(id);

    if (!targetFieldResponse) {
      console.error(
        "LogicModifier.logicModificationResultForRecapResponse unable to find target response"
      );
      console.info("target:", this.target);
      console.info("recapResponse:", recapResponse);
      return;
    }
    const targetFieldInstance =
      targetFieldResponse && targetFieldResponse.fieldInstance;
    const targetField = targetFieldInstance && targetFieldInstance.field;
    const targetFieldType = targetField && targetField.fieldType;

    if (!targetFieldType) {
      console.error(
        "LogicModifier.logicModificationResultForRecapResponse unable to find target field type! Maybe targetFieldResponse is missing? Looking for targetFieldResponse.fieldInstance.field.fieldType"
      );
      console.info("target:", this.target);
      console.info("recapResponse:", recapResponse);
      console.info("targetFieldResponse:", targetFieldResponse);
      return;
    }

    let logicResult;
    if (this.logic === "PRESENT") {
      logicResult = !targetFieldResponse.isEmpty;
    } else {
      if (targetFieldType.isBoolean) {
        logicResult = this._logicResultForBooleanFieldResponse(
          targetFieldResponse,
          targetFieldType
        );
      } else if (targetFieldType.has_options) {
        logicResult = this._logicResultForSelectFieldResponse(
          targetFieldResponse,
          targetFieldType
        );
      } else {
        logicResult = this._logicResultForFieldResponse(
          targetFieldResponse,
          targetFieldType
        );
      }
    }

    if (logicResult) {
      modifyFieldResponse.fieldInstance.field.filteredOptions =
        this.truthy_values;
    } else {
      modifyFieldResponse.fieldInstance.field.filteredOptions =
        this.falsy_values;
    }
  }

  _logicResultForBooleanFieldResponse(targetFieldResponse, targetFieldType) {
    let logicResult;
    let targetValue =
      (targetFieldResponse.currentValue &&
        convertStringToBoolean(targetFieldResponse.currentValue.toString())) ||
      false;
    let myValue = convertStringToBoolean(this.value);
    if (this.logic === "EQUALS") {
      logicResult = !targetFieldResponse.isEmpty && targetValue === myValue;
    } else if (this.logic === "DOES_NOT_EQUAL") {
      logicResult = !targetFieldResponse.isEmpty && targetValue !== myValue;
    } else {
      console.warn(
        "Unsupported logic type for boolean field:",
        targetFieldType.defined_as,
        targetFieldResponse,
        this
      );
      logicResult = false;
    }
    return logicResult;
  }

  _logicResultForSelectFieldResponse(targetFieldResponse, targetFieldType) {
    let logicResult;
    const targetOptionIds = (
        targetFieldResponse.currentValue &&
        targetFieldResponse.currentValue.split &&
        selectedOptionTokens(targetFieldResponse.currentValue).map(normalizeIdentifier)
      )
      || [];
    let myOptionId =
      this.valueOption && normalizeIdentifier(this.valueOption.id);
    if (this.logic === "EQUALS") {
      logicResult =
        !targetFieldResponse.isEmpty &&
        targetOptionIds.indexOf(myOptionId) >= 0;
    } else if (this.logic === "DOES_NOT_EQUAL") {
      logicResult =
        !targetFieldResponse.isEmpty && targetOptionIds.indexOf(myOptionId) < 0;
    } else {
      console.warn(
        "Unsupported logic type for field with select options:",
        targetFieldType.defined_as,
        targetFieldResponse,
        this
      );
      logicResult = false;
    }
    return logicResult;
  }

  _logicResultForFieldResponse(targetFieldResponse, targetFieldType) {
    let logicResult;

    /**
     * Define comparator methods that can be customized for different field types
     */
    let isEqual, isNotEqual, isGreaterThan, isLessThan;

    /**
     * Standard comparisons use javascript === for equal-to and > for greater-than
     */
    isEqual = this._compareIsEqual;
    isGreaterThan = this._compareIsGreaterThan;

    /**
     * Define less-than and not-equal-to in terms of equal-to and greater-than
     * @param targetValue
     * @param myValue
     */
    isLessThan = (targetValue, myValue) =>
      !isGreaterThan(targetValue, myValue) && !isEqual(targetValue, myValue);
    isNotEqual = (targetValue, myValue) => !isEqual(targetValue, myValue);

    let targetValue = targetFieldResponse.currentValue;
    let myValue = this.value;

    // Convert myValue to integer or float if necessary
    if (targetFieldType.isInteger) {
      myValue = parseInt(myValue);
    } else if (targetFieldType.isFloat) {
      myValue = parseFloat(myValue);
    }

    /**
     * Convert date fields before comparison
     */
    let myValueMomentString;
    let targetValueMomentString;
    if (targetFieldType.isTime) {
      targetValueMomentString = moment(targetValue, "h:mma").format(
        "YYYY-MM-DD HH:mm"
      );
      myValueMomentString = moment(myValue, "h:mma").format("YYYY-MM-DD HH:mm");
    }
    if (targetFieldType.isDate || targetFieldType.isDateTime) {
      targetValueMomentString = moment(targetValue).format("YYYY-MM-DD HH:mm");
      myValueMomentString = moment(myValue).format("YYYY-MM-DD HH:mm");
    }

    const eventTimezoneName =
      targetFieldResponse.recapResponse.eventTimezoneName;
    if (
      targetFieldType &&
      (targetFieldType.isDateTime ||
        targetFieldType.isTime ||
        targetFieldType.isDate)
    ) {
      // Date or time
      let myValueParsed;
      if (targetFieldType.isDateTime) {
        myValueParsed =
          this.DateTimeService.parseDateWithTimezoneOrServerTimezone(
            myValue,
            eventTimezoneName
          );
      } else if (targetFieldType.isTime) {
        myValueParsed =
          this.DateTimeService.parseTimeWithTimezoneOrServerTimezone(
            myValue,
            targetValue,
            eventTimezoneName
          );
      } else if (targetFieldType.isDate) {
        myValueParsed = this.DateTimeService.parseDate(myValue);
      }

      // Strip off seconds and ms for logic comparisons with times and datetimes
      if (targetFieldType.isDateTime || targetFieldType.isTime) {
        if (isValidDateObject(myValueParsed)) {
          myValueParsed.setSeconds(0);
          myValueParsed.setMilliseconds(0);
        }
        if (isValidDateObject(targetValue)) {
          targetValue.setSeconds(0);
          targetValue.setMilliseconds(0);
        }
      }

      // Convert my value and target value to time in ms
      if (isValidDateObject(myValueParsed)) {
        myValue = myValueParsed.getTime();
      }
      if (isValidDateObject(targetValue)) {
        targetValue = targetValue.getTime();
      }
    }

    if (this.logic === "EQUALS") {
      if (
        targetFieldType.isDate ||
        targetFieldType.isDateTime ||
        targetFieldType.isTime
      ) {
        logicResult =
          !targetFieldResponse.isEmpty &&
          datesAreEqual(myValueMomentString, targetValueMomentString);
      } else {
        logicResult =
          !targetFieldResponse.isEmpty && isEqual(targetValue, myValue);
      }
    } else if (this.logic === "DOES_NOT_EQUAL") {
      if (
        targetFieldType.isDate ||
        targetFieldType.isDateTime ||
        targetFieldType.isTime
      ) {
        logicResult =
          !targetFieldResponse.isEmpty &&
          !datesAreEqual(myValueMomentString, targetValueMomentString);
      } else {
        logicResult =
          !targetFieldResponse.isEmpty && isNotEqual(targetValue, myValue);
      }
    } else if (this.logic === "GREATER_THAN") {
      logicResult =
        !targetFieldResponse.isEmpty && isGreaterThan(targetValue, myValue);
    } else if (this.logic === "LESS_THAN") {
      logicResult =
        !targetFieldResponse.isEmpty && isLessThan(targetValue, myValue);
    } else if (this.logic === "BEFORE_EX") {
      logicResult =
        !targetFieldResponse.isEmpty &&
        dateIsBefore(targetValueMomentString, myValueMomentString);
    } else if (this.logic === "BEFORE_IN") {
      logicResult =
        !targetFieldResponse.isEmpty &&
        (dateIsBefore(targetValueMomentString, myValueMomentString) ||
          datesAreEqual(myValueMomentString, targetValueMomentString));
    } else if (this.logic === "AFTER_EX") {
      logicResult =
        !targetFieldResponse.isEmpty &&
        dateIsAfter(targetValueMomentString, myValueMomentString);
    } else if (this.logic === "AFTER_IN") {
      logicResult =
        !targetFieldResponse.isEmpty &&
        (dateIsAfter(targetValueMomentString, myValueMomentString) ||
          datesAreEqual(myValueMomentString, targetValueMomentString));
    }
    return logicResult;
  }

  _compareDurationIsEqual(targetValue, myValue) {
    let [targetHH, targetMM] = parseDurationStringIntoHHMM(targetValue);
    let [myHH, myMM] = parseDurationStringIntoHHMM(myValue);
    return targetHH === myHH && targetMM === myMM;
  }

  _compareDurationIsGreaterThan(targetValue, myValue) {
    let [targetHH, targetMM] = parseDurationStringIntoHHMM(targetValue);
    let [myHH, myMM] = parseDurationStringIntoHHMM(myValue);
    return targetHH > myHH || (targetHH === myHH && targetMM > myMM);
  }

  _compareIsEqual(targetValue, myValue) {
    return targetValue === myValue;
  }

  _compareIsGreaterThan(targetValue, myValue) {
    return targetValue > myValue;
  }
}

export default LogicModifier;
